import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { createContext } from 'react';

import { i18nBunja } from '@flipster/i18n';

import { isActionAllowedForRegulation } from '../../../currency/is-action-allowed-for-regulation';
import { v1CurrenciesBunja } from '../../../currency/use-v1-currencies';
import { userRegulationBunja } from '../../../regulation';

export type WalletModalType = 'withdraw' | 'deposit' | 'convert';
export const WalletModalTypeContext = createContext<WalletModalType>(null!);
export const WalletModalTypeScope = createScopeFromContext(WalletModalTypeContext);

export interface WalletModalOptions {
  defaultValues: {
    // FIXME: separate this part;
    currency?: string; // deposit
    toCurrency?: string; // conversion
    percent?: string;
  };
}
export const WalletModalOptionsContext = createContext<WalletModalOptions>(null!);
export const WalletModalOptionsScope = createScopeFromContext(WalletModalOptionsContext);

export const walletModalBunja = bunja(() => {
  const { getT } = bunja.use(i18nBunja);
  const t = getT();
  const type = bunja.use(WalletModalTypeScope);
  const options = bunja.use(WalletModalOptionsScope);
  const { currenciesKeyByCurrencyAtom } = bunja.use(v1CurrenciesBunja);
  const { getUserRegulationAtom } = bunja.use(userRegulationBunja);
  const selectedCurrencyAtom = atom<string | null>(options.defaultValues.currency ?? null);
  const rawSelectedNetworkAtom = atom<string | null>(null);
  const selectedNetworkAtom = atom(
    (get) => {
      const raw = get(rawSelectedNetworkAtom);
      if (!raw) return get(networkItemsAtom)[0]?.value;
      return raw;
    },
    (_, set, network?: string) => {
      set(rawSelectedNetworkAtom, network ?? null);
    },
  );
  const selectedCurrencyItemAtom = atom((get) => {
    return get(currenciesKeyByCurrencyAtom)?.[get(selectedCurrencyAtom) ?? ''];
  });

  const networkItemsAtom = atom((get) => {
    const selectedCurrency = get(selectedCurrencyAtom);
    const currenciesKeyByCurrency = get(currenciesKeyByCurrencyAtom);
    const regulationStatus = get(getUserRegulationAtom).data?.regulationStatus ?? null;
    if (!selectedCurrency || type === 'convert') return [];
    return (currenciesKeyByCurrency?.[selectedCurrency]?.blockchainInfo ?? []).reduce<
      {
        label: string;
        value: string;
        blockchainName: string;
        isDestinationTagRequired: boolean;
        isMemoRequired: boolean;
        withdrawalFee: string;
        withdrawalPrecision: number;
      }[]
    >((acc, cur) => {
      if (selectedCurrency !== cur.blockchainAsset) return acc;
      if (
        type === 'deposit' &&
        (!isActionAllowedForRegulation(cur.depositAllowedRegulationStatuses, regulationStatus) || !cur.depositAvailable)
      )
        return acc;
      if (
        type === 'withdraw' &&
        !isActionAllowedForRegulation(cur.withdrawalAllowedRegulationStatuses, regulationStatus)
      )
        return acc;
      return [
        ...acc,
        {
          label: cur.networkName === 'INTERNAL' ? t('Common.r251222.Internal_flipster') : cur.networkName,
          value: cur.networkName,
          blockchainName: cur.blockchainName,
          isDestinationTagRequired: cur.addressParams.includes('destinationTag'),
          isMemoRequired: cur.addressParams.includes('memo'),
          withdrawalFee: cur.withdrawalFee,
          withdrawalPrecision: cur.withdrawalPrecision,
        },
      ];
    }, []);
  });

  return {
    type,
    options,
    networkItemsAtom,
    selectedCurrencyAtom,
    selectedCurrencyItemAtom,
    selectedNetworkAtom,
  };
});
walletModalBunja.debugLabel = 'walletModalBunja';
